import { ARCH_CURVE } from '../../core/params/curve';
import { DEFAULT_PALETTE } from '../../core/palette';
import type { ParamSchema, SchemaValues } from '../../core/params/types';

export const SKELETON_PARAMS = [
  {
    kind: 'float',
    key: 'resolution',
    label: 'Abtastweite',
    min: 1,
    max: 12,
    step: 0.5,
    default: 2.5,
    unit: 'px',
    neverRandomise: true,
    group: 'Skelett',
    tooltip: 'Gitterweite der Mittelachsen-Erkennung. Feiner ist genauer und langsamer.',
  },
  {
    kind: 'float',
    key: 'threshold',
    label: 'Erkennungsschwelle',
    min: 0.02,
    max: 0.9,
    step: 0.01,
    default: 0.3,
    group: 'Skelett',
    tooltip: 'Wie ausgeprägt ein lokales Distanzmaximum sein muss, um als Mittelachse zu gelten.',
  },
  {
    kind: 'float',
    key: 'simplify',
    label: 'Vereinfachung',
    min: 0,
    max: 20,
    step: 0.5,
    default: 2,
    unit: 'px',
    group: 'Skelett',
    tooltip: 'Douglas-Peucker-Toleranz für die Strichzüge.',
  },
  {
    kind: 'float',
    key: 'minLength',
    label: 'Mindestlänge',
    min: 0,
    max: 200,
    step: 1,
    // Low by default: crossbars and bowls have much shorter ridges than stems, and a
    // higher threshold silently drops them — leaving only the vertical strokes.
    default: 5,
    unit: 'px',
    group: 'Skelett',
    tooltip: 'Kurze Aststummel werden verworfen — sie sind meist Rauschen der Kontur.',
  },

  {
    kind: 'float',
    key: 'width',
    label: 'Strichstärke',
    min: 0.3,
    max: 80,
    step: 0.5,
    scale: 'log',
    default: 9,
    unit: 'px',
    bulkRange: [2, 30],
    group: 'Darstellung',
  },
  {
    kind: 'curve',
    key: 'widthCurve',
    label: 'Strichstärken-Kurve',
    default: ARCH_CURVE,
    xLabel: 'Anfang',
    yLabel: 'Ende',
    group: 'Darstellung',
  },
  {
    kind: 'bool',
    key: 'widthFromDistance',
    label: 'Stärke aus der Glyphendicke',
    default: true,
    group: 'Darstellung',
    tooltip: 'Der Strich wird dort dick, wo der Buchstabe dick ist.',
  },
  {
    kind: 'enum',
    key: 'cap',
    label: 'Endkappen',
    default: 'round',
    segmented: true,
    group: 'Darstellung',
    options: [
      { value: 'butt', label: 'Flach' },
      { value: 'round', label: 'Rund' },
      { value: 'square', label: 'Quadrat' },
    ],
  },
  {
    kind: 'float',
    key: 'jitter',
    label: 'Zittern',
    min: 0,
    max: 24,
    step: 0.25,
    default: 0,
    unit: 'px',
    group: 'Darstellung',
  },
  {
    kind: 'float',
    key: 'opacity',
    label: 'Deckkraft',
    min: 0.05,
    max: 1,
    step: 0.01,
    default: 1,
    group: 'Darstellung',
  },

  {
    kind: 'enum',
    key: 'colorMode',
    label: 'Farbmodus',
    default: 'thickness',
    group: 'Farbe',
    options: [
      { value: 'solid', label: 'Einfarbig' },
      { value: 'thickness', label: 'Nach Dicke' },
      { value: 'stroke', label: 'Nach Strichzug' },
    ],
  },
  {
    kind: 'palette',
    key: 'palette',
    label: 'Palette',
    default: { ...DEFAULT_PALETTE, paletteId: 'acid' },
    group: 'Farbe',
  },
] as const satisfies ParamSchema;

export type SkeletonValues = SchemaValues<typeof SKELETON_PARAMS>;
